<script setup>
import { ref, computed, onMounted } from 'vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from '@/stores/store';
import { useToast } from '@/composables/useToast.js';
import {
  createLocation,
  postWarehouse,
  createArea,
  createCategory,
  createItem,
  createItemInstance,
} from '@/api/api.js';
import MdiMapMarker from '~icons/mdi/map-marker';
import MdiWarehouse from '~icons/mdi/warehouse';
import MdiViewGrid from '~icons/mdi/view-grid';
import MdiTag from '~icons/mdi/tag';
import MdiPackageVariant from '~icons/mdi/package-variant';
import MdiCheckCircle from '~icons/mdi/check-circle';
import MdiChevronRight from '~icons/mdi/chevron-right';
import MdiClose from '~icons/mdi/close';
import MdiRocketLaunch from '~icons/mdi/rocket-launch';
import MdiArrowRight from '~icons/mdi/arrow-right';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const toast = useToast();

// Hard guard — refuse to operate if locations already exist
onMounted(() => {
  if (store.locations.length > 0) {
    emit('close');
  }
});

// ── State ─────────────────────────────────────────────────────────────────────

const step = ref(0); // 0 = welcome, 1–5 = forms, 6 = done
const saving = ref(false);
const stepError = ref(null);

const locationForm = ref({ name: '', address: '', description: '' });
const warehouseForm = ref({ name: '' });
const areaForm = ref({ name: '' });
const categoryForm = ref({ name: '' });
const itemForm = ref({ name: '', item_type: 1, available_quantity: '' });

const createdLocation = ref(null);
const createdWarehouse = ref(null);
const createdArea = ref(null);
const createdCategory = ref(null);
const createdItem = ref(null);

// ── Step metadata ─────────────────────────────────────────────────────────────

const TOTAL_STEPS = 5;

const steps = computed(() => [
  { label: t('common.location'),         icon: MdiMapMarker },
  { label: t('common.warehouse'),        icon: MdiWarehouse },
  { label: t('common.area'),             icon: MdiViewGrid },
  { label: t('entity.category'),         icon: MdiTag },
  { label: t('common.item'),             icon: MdiPackageVariant },
]);

// Welcome screen checklist (mirrors steps but with richer descriptions)
const welcomeSteps = computed(() => [
  { icon: MdiMapMarker,      label: t('common.location'),             description: t('onboarding.welcomeLocationDesc') },
  { icon: MdiWarehouse,      label: t('common.warehouse'),            description: t('onboarding.welcomeWarehouseDesc') },
  { icon: MdiViewGrid,       label: t('common.area'),                 description: t('onboarding.welcomeAreaDesc') },
  { icon: MdiTag,            label: t('entity.category'),             description: t('onboarding.welcomeCategoryDesc') },
  { icon: MdiPackageVariant, label: t('onboarding.welcomeItemLabel'), description: t('onboarding.welcomeItemDesc') },
]);

const itemTypeOptions = computed(() => [
  { value: 1, label: t('itemTypes.unique'),     sub: t('onboarding.itemTypeSub1') },
  { value: 2, label: t('itemTypes.multi'),      sub: t('onboarding.itemTypeSub2') },
  { value: 3, label: t('itemTypes.quantity'),   sub: t('onboarding.itemTypeSub3') },
  { value: 4, label: t('itemTypes.consumable'), sub: t('onboarding.itemTypeSub4') },
]);

const needsQuantity = computed(() =>
  itemForm.value.item_type === 3 || itemForm.value.item_type === 4
);

// ── Validation ────────────────────────────────────────────────────────────────

const canProceed = computed(() => {
  switch (step.value) {
    case 1: return locationForm.value.name.trim() && locationForm.value.address.trim();
    case 2: return warehouseForm.value.name.trim();
    case 3: return areaForm.value.name.trim();
    case 4: return categoryForm.value.name.trim();
    case 5: {
      const f = itemForm.value;
      return f.name.trim() && (!needsQuantity.value || String(f.available_quantity).trim() !== '');
    }
    default: return false;
  }
});

// ── Actions ───────────────────────────────────────────────────────────────────

async function nextStep() {
  if (!canProceed.value || saving.value) return;
  saving.value = true;
  stepError.value = null;

  try {
    if (step.value === 1) {
      const payload = {
        name: locationForm.value.name.trim(),
        address: locationForm.value.address.trim(),
      };
      if (locationForm.value.description.trim()) {
        payload.description = locationForm.value.description.trim();
      }
      createdLocation.value = await createLocation(payload);

    } else if (step.value === 2) {
      createdWarehouse.value = await postWarehouse({
        name: warehouseForm.value.name.trim(),
        location: createdLocation.value.id,
      });

    } else if (step.value === 3) {
      createdArea.value = await createArea({
        name: areaForm.value.name.trim(),
        warehouse: createdWarehouse.value.id,
        location: createdLocation.value.id,
      });

    } else if (step.value === 4) {
      createdCategory.value = await createCategory({
        name: categoryForm.value.name.trim(),
        parent: null,
      });

    } else if (step.value === 5) {
      const f = itemForm.value;
      createdItem.value = await createItem({
        name: f.name.trim(),
        category: createdCategory.value.id,
        item_type: f.item_type,
      });
      const instancePayload = {
        item: createdItem.value.id,
        area: createdArea.value.id,
        status: 1,
      };
      if (needsQuantity.value) {
        instancePayload.available_quantity = Number(f.available_quantity);
      }
      await createItemInstance(instancePayload);

      // Refresh store so the rest of the app picks up the new data
      await Promise.allSettled([
        store.fetchLocations(true),
        store.fetchCategories(true),
      ]);
    }

    step.value++;

  } catch (err) {
    stepError.value =
      err?.response?.data?.detail ??
      err?.message ??
      t('onboarding.saveError');
  } finally {
    saving.value = false;
  }
}

function close() {
  emit('close');
}

function viewItems() {
  emit('close');
  router.push('/items');
}
</script>

<template>
  <!-- Backdrop -->
  <div class="modal-overlay" @click.self="close">

    <!-- Wizard card -->
    <div
      class="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
      style="background: var(--color-surface); border: 1px solid var(--color-border);"
    >

      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-4 shrink-0"
        style="border-bottom: 1px solid var(--color-border);"
      >
        <div>
          <p class="text-xs font-semibold uppercase tracking-widest" style="color: var(--color-accent);">
            {{ $t('onboarding.workspaceSetup') }}
          </p>
          <h2 class="text-base font-bold mt-0.5" style="color: var(--color-text);">
            <template v-if="step === 0">{{ $t('onboarding.getStartedTitle') }}</template>
            <template v-else-if="step <= TOTAL_STEPS">{{ steps[step - 1].label }}</template>
            <template v-else>{{ $t('onboarding.allDone') }}</template>
          </h2>
        </div>
        <button
          class="btn-icon"
          :disabled="saving"
          :title="$t('common.close')"
          @click="close"
        >
          <MdiClose class="w-4 h-4" />
        </button>
      </div>

      <!-- Progress dots (only shown during steps 1–5) -->
      <div v-if="step >= 1 && step <= TOTAL_STEPS" class="flex items-center justify-center gap-2 px-5 py-3 shrink-0">
        <template v-for="(s, i) in steps" :key="i">
          <div
            class="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all duration-200"
            :style="
              step > i + 1
                ? 'background: var(--color-accent); color: #0f1a14;'
                : step === i + 1
                  ? 'background: var(--color-primary); color: var(--color-accent); outline: 2px solid var(--color-accent); outline-offset: 2px;'
                  : 'background: var(--color-surface-2); color: var(--color-muted);'
            "
          >
            <MdiCheckCircle v-if="step > i + 1" class="w-4 h-4" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <MdiChevronRight
            v-if="i < steps.length - 1"
            class="w-4 h-4 shrink-0"
            style="color: var(--color-border);"
          />
        </template>
      </div>

      <!-- Step content -->
      <div class="flex-1 overflow-y-auto px-5 py-4">

        <!-- Step 0: Welcome -->
        <div v-if="step === 0" class="space-y-5">
          <div class="flex justify-center py-2">
            <div class="flex items-center justify-center w-16 h-16 rounded-2xl"
                 style="background: rgba(168,208,193,0.1); border: 1px solid rgba(168,208,193,0.2);">
              <MdiRocketLaunch class="w-8 h-8" style="color: var(--color-accent);" />
            </div>
          </div>
          <div class="text-center">
            <h3 class="text-lg font-bold" style="color: var(--color-text);">{{ $t('onboarding.welcomeTitle') }}</h3>
            <p class="text-sm mt-1" style="color: var(--color-muted);">{{ $t('onboarding.welcomeBody') }}</p>
          </div>
          <div class="rounded-xl p-4 space-y-3" style="background: var(--color-surface-2); border: 1px solid var(--color-border);">
            <p class="text-xs font-semibold uppercase tracking-widest mb-1" style="color: var(--color-muted);">{{ $t('onboarding.whatWeSetup') }}</p>
            <div v-for="item in welcomeSteps" :key="item.label" class="flex items-start gap-3">
              <div class="flex items-center justify-center w-7 h-7 rounded-full shrink-0 mt-0.5"
                   style="background: rgba(168,208,193,0.1);">
                <component :is="item.icon" class="w-4 h-4" style="color: var(--color-accent);" />
              </div>
              <div class="min-w-0">
                <p class="text-sm font-medium" style="color: var(--color-text);">{{ item.label }}</p>
                <p class="text-xs mt-0.5" style="color: var(--color-muted);">{{ item.description }}</p>
              </div>
            </div>
          </div>
          <p class="text-xs text-center" style="color: var(--color-muted);">{{ $t('onboarding.addMoreLater') }}</p>
        </div>

        <!-- Step 1: Location -->
        <div v-if="step === 1" class="space-y-4">
          <p class="text-sm" style="color: var(--color-muted);">{{ $t('onboarding.locationIntro') }}</p>
          <BaseFormField :label="$t('onboarding.locationName')" required>
            <BaseInput
              v-model="locationForm.name"
              type="text"
              placeholder="Lager Oslo"
              @keydown.enter="canProceed && nextStep()"
            />
          </BaseFormField>
          <BaseFormField :label="$t('onboarding.address')" required>
            <BaseInput
              v-model="locationForm.address"
              type="text"
              placeholder="Storgata 1, 0155 Oslo"
              @keydown.enter="canProceed && nextStep()"
            />
          </BaseFormField>
          <BaseFormField :label="$t('onboarding.descriptionLabel')">
            <BaseInput
              v-model="locationForm.description"
              type="text"
              placeholder="Hovudkontor"
            />
          </BaseFormField>
        </div>

        <!-- Step 2: Warehouse -->
        <div v-else-if="step === 2" class="space-y-4">
          <p class="text-sm" style="color: var(--color-muted);">{{ $t('onboarding.warehouseIntro', { name: createdLocation?.name }) }}</p>
          <BaseFormField :label="$t('onboarding.warehouseName')" required>
            <BaseInput
              v-model="warehouseForm.name"
              type="text"
              placeholder="Hoveddepot"
              @keydown.enter="canProceed && nextStep()"
            />
          </BaseFormField>
        </div>

        <!-- Step 3: Area -->
        <div v-else-if="step === 3" class="space-y-4">
          <p class="text-sm" style="color: var(--color-muted);">{{ $t('onboarding.areaIntro', { name: createdWarehouse?.name }) }}</p>
          <BaseFormField :label="$t('onboarding.areaName')" required>
            <BaseInput
              v-model="areaForm.name"
              type="text"
              placeholder="Hylle A1"
              @keydown.enter="canProceed && nextStep()"
            />
          </BaseFormField>
        </div>

        <!-- Step 4: Category -->
        <div v-else-if="step === 4" class="space-y-4">
          <p class="text-sm" style="color: var(--color-muted);">{{ $t('onboarding.categoryDesc') }}</p>
          <BaseFormField :label="$t('onboarding.categoryName')" required>
            <BaseInput
              v-model="categoryForm.name"
              type="text"
              placeholder="Lyd og musikk"
              @keydown.enter="canProceed && nextStep()"
            />
          </BaseFormField>
        </div>

        <!-- Step 5: Item -->
        <div v-else-if="step === 5" class="space-y-4">
          <p class="text-sm" style="color: var(--color-muted);">{{ $t('onboarding.itemDesc', { area: createdArea?.name, category: createdCategory?.name }) }}</p>
          <BaseFormField :label="$t('onboarding.itemName')" required>
            <BaseInput
              v-model="itemForm.name"
              type="text"
              placeholder="Mikrofon Shure SM58"
            />
          </BaseFormField>
          <BaseFormField :label="$t('onboarding.itemTypeLabel')" required>
            <BaseSelect v-model="itemForm.item_type">
              <option
                v-for="opt in itemTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }} — {{ opt.sub }}
              </option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField v-if="needsQuantity" :label="$t('onboarding.startingQuantity')" required>
            <BaseInput
              v-model="itemForm.available_quantity"
              type="number"
              min="0"
              placeholder="10"
            />
          </BaseFormField>
        </div>

        <!-- Step 6: Done -->
        <div v-else-if="step === 6" class="space-y-4 text-center py-2">
          <MdiCheckCircle class="w-14 h-14 mx-auto" style="color: var(--color-accent);" />
          <div>
            <h3 class="text-lg font-bold" style="color: var(--color-text);">{{ $t('onboarding.workspaceReady') }}</h3>
            <p class="text-sm mt-1" style="color: var(--color-muted);">{{ $t('onboarding.hereIsWhat') }}</p>
          </div>
          <ul class="text-sm text-left rounded-xl p-4 space-y-2" style="background: var(--color-surface-2); border: 1px solid var(--color-border);">
            <li class="flex items-center gap-2">
              <MdiMapMarker class="w-4 h-4 shrink-0" style="color: var(--color-accent);" />
              <span style="color: var(--color-muted);">{{ $t('common.location') }}:</span>
              <span class="font-medium" style="color: var(--color-text);">{{ createdLocation?.name }}</span>
            </li>
            <li class="flex items-center gap-2">
              <MdiWarehouse class="w-4 h-4 shrink-0" style="color: var(--color-accent);" />
              <span style="color: var(--color-muted);">{{ $t('common.warehouse') }}:</span>
              <span class="font-medium" style="color: var(--color-text);">{{ createdWarehouse?.name }}</span>
            </li>
            <li class="flex items-center gap-2">
              <MdiViewGrid class="w-4 h-4 shrink-0" style="color: var(--color-accent);" />
              <span style="color: var(--color-muted);">{{ $t('common.area') }}:</span>
              <span class="font-medium" style="color: var(--color-text);">{{ createdArea?.name }}</span>
            </li>
            <li class="flex items-center gap-2">
              <MdiTag class="w-4 h-4 shrink-0" style="color: var(--color-accent);" />
              <span style="color: var(--color-muted);">{{ $t('entity.category') }}:</span>
              <span class="font-medium" style="color: var(--color-text);">{{ createdCategory?.name }}</span>
            </li>
            <li class="flex items-center gap-2">
              <MdiPackageVariant class="w-4 h-4 shrink-0" style="color: var(--color-accent);" />
              <span style="color: var(--color-muted);">{{ $t('common.item') }}:</span>
              <span class="font-medium" style="color: var(--color-text);">{{ createdItem?.name }}</span>
            </li>
          </ul>
        </div>

      </div>

      <!-- Error -->
      <div
        v-if="stepError"
        class="mx-5 mb-2 px-4 py-2 rounded-lg text-sm shrink-0"
        style="background: rgba(239,68,68,0.12); color: #f87171; border: 1px solid rgba(239,68,68,0.25);"
      >
        {{ stepError }}
      </div>

      <!-- Footer actions -->
      <div
        class="flex items-center justify-between gap-3 px-5 py-4 shrink-0"
        style="border-top: 1px solid var(--color-border);"
      >
        <!-- Left: step counter or skip hint -->
        <span class="text-xs" style="color: var(--color-muted);">
          <template v-if="step >= 1 && step <= TOTAL_STEPS">{{ $t('onboarding.stepOf', { step, total: TOTAL_STEPS }) }}</template>
        </span>

        <!-- Right: action buttons -->
        <div class="flex items-center gap-2">
          <!-- Step 0: Welcome -->
          <template v-if="step === 0">
            <button class="btn ghost small" @click="close">{{ $t('onboarding.maybeLater') }}</button>
            <button class="btn primary small" @click="step = 1">
              <MdiArrowRight class="w-4 h-4 mr-1" />
              {{ $t('onboarding.letsGo') }}
            </button>
          </template>

          <!-- Steps 1–5: Next -->
          <template v-else-if="step <= TOTAL_STEPS">
            <button
              class="btn ghost small"
              :disabled="saving"
              @click="close"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              class="btn primary small"
              :disabled="!canProceed || saving"
              @click="nextStep"
            >
              <span v-if="saving">{{ $t('common.saving') }}</span>
              <span v-else-if="step === TOTAL_STEPS">{{ $t('onboarding.finish') }}</span>
              <span v-else>{{ $t('common.next') }}</span>
            </button>
          </template>

          <!-- Step 6: Done/View -->
          <template v-else>
            <button class="btn ghost small" @click="close">{{ $t('common.close') }}</button>
            <button class="btn primary small" @click="viewItems">{{ $t('onboarding.viewItems') }}</button>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>
